import type { CapabilityScope } from './auth';
import { AuthenticationMethod, Capabilities } from './auth';
import { InputContentType, ListContentType, OutputContentType } from './content-types';
import { HTTPMethod } from './http-methods';
import type { RouteNames } from './route-names';

// this is required to make typescript use tuples
const params = <T extends string[]>(...param: T) => param;

// This enforces the shape of our routes object without blowing up tuples
const makeRoutes = <
    R extends {
        [Route in RouteNames]: {
            path: string;
            method: HTTPMethod;
            params: string[];
            auth: AuthenticationMethod;
            allowedCapabilities?: readonly CapabilityScope[];
            // These are not all accurate, but huge amount of manual labor to pull out of Eden
            // Will be updated as needed
            inputType: InputContentType | ListContentType<InputContentType>;
        } & (
            | {
                  outputType: OutputContentType;
                  paginate: false;
              }
            | { outputType: ListContentType<OutputContentType>; paginate: boolean }
        );
    },
>(
    rs: R,
) => rs;

export const routes = makeRoutes({
    post_client_authenticate: {
        path: '/client-authenticate',
        method: HTTPMethod.POST,
        params: params(),
        auth: AuthenticationMethod.NONE,
        inputType: InputContentType.POST_CLIENT_AUTHENTICATE,
        outputType: OutputContentType.CLIENT_TOKEN,
        paginate: false,
    },
    post_user_authenticate: {
        path: '/user-authenticate',
        method: HTTPMethod.POST,
        params: params(),
        auth: AuthenticationMethod.CLIENT,
        inputType: InputContentType.POST_USER_AUTHENTICATE,
        outputType: OutputContentType.USER_TOKEN,
        paginate: false,
    },
    post_user_authenticate_from_token: {
        path: '/user-authenticate-from-token',
        method: HTTPMethod.POST,
        params: params(),
        auth: AuthenticationMethod.CLIENT,
        inputType: InputContentType.POST_USER_AUTHENTICATE_FROM_TOKEN,
        outputType: OutputContentType.USER_TOKEN,
        paginate: false,
    },
    post_users: {
        path: '/users/',
        method: HTTPMethod.POST,
        params: params(),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    put_manager: {
        path: '/teams/:teamID/managers/',
        method: HTTPMethod.PUT,
        params: params('teamID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.PUT_TEAM_MANAGER,
        outputType: OutputContentType.TEAM_USER,
        paginate: false,
    },
    delete_manager: {
        path: '/teams/:teamID/managers/:userID',
        method: HTTPMethod.DELETE,
        params: params('teamID', 'userID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    get_user: {
        path: '/users/:userId',
        method: HTTPMethod.GET,
        params: params('userId'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    get_user_status: {
        path: '/users/:email/status',
        method: HTTPMethod.GET,
        params: params('email'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    send_login_link: {
        path: '/users/:email/send-login-link',
        method: HTTPMethod.POST,
        params: params('email'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.POST_LOGIN_LINK,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    'send-email-confirmation': {
        path: '/users/:email/send-email-confirmation',
        method: HTTPMethod.POST,
        params: params('email'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    post_teams: {
        path: '/teams/',
        method: HTTPMethod.POST,
        params: params(),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.POST_TEAM,
        outputType: OutputContentType.TEAM,
        paginate: false,
    },
    get_team: {
        path: '/teams/:teamID',
        method: HTTPMethod.GET,
        params: params('teamID'),
        auth: AuthenticationMethod.USER,
        allowedCapabilities: Capabilities.canAccessTeam,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.TEAM,
        paginate: false,
    },
    patch_team: {
        path: '/teams/:teamID',
        method: HTTPMethod.PATCH,
        params: params('teamID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.TEAM,
        paginate: false,
    },
    get_team_associations: {
        path: '/teams/:teamID/associations',
        method: HTTPMethod.GET,
        params: params('teamID'),
        auth: AuthenticationMethod.USER,
        allowedCapabilities: Capabilities.canAccessRSVP,
        inputType: InputContentType.NONE,
        outputType: new ListContentType(OutputContentType.USER_TEAM_ASSOCIATIONS),
        paginate: true,
    },
    get_team_fans: {
        path: '/teams/:teamID/fans',
        method: HTTPMethod.GET,
        params: params('teamID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: new ListContentType(OutputContentType.TEAM_FAN),
        paginate: true,
    },
    get_team_relationships: {
        path: '/teams/:teamID/relationships',
        method: HTTPMethod.GET,
        params: params('teamID'),
        auth: AuthenticationMethod.USER,
        allowedCapabilities: Capabilities.canAccessRSVP,
        inputType: InputContentType.NONE,
        outputType: new ListContentType(OutputContentType.USER_PLAYER_RELATIONSHIP),
        paginate: true,
    },
    get_team_users: {
        path: '/teams/:teamID/users',
        method: HTTPMethod.GET,
        params: params('teamID'),
        allowedCapabilities: Capabilities.canAccessRSVP,
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: new ListContentType(OutputContentType.TEAM_USER),
        paginate: true,
    },
    get_messaging_channel: {
        path: '/teams/:teamID/messaging-channel',
        method: HTTPMethod.GET,
        params: params('teamID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    post_team_avatar_image: {
        path: '/teams/:teamID/avatar-image',
        method: HTTPMethod.POST,
        params: params('teamID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    delete_team_avatar_image: {
        path: '/teams/:teamID/avatar-image',
        method: HTTPMethod.DELETE,
        params: params('teamID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    get_team_avatar_image: {
        path: '/teams/:teamID/avatar-image',
        method: HTTPMethod.GET,
        params: params('teamID'),
        auth: AuthenticationMethod.USER,
        allowedCapabilities: Capabilities.canAccessTeam,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.TEAM_AVATAR_IMAGE,
        paginate: false,
    },
    get_team_notification_setting: {
        path: '/teams/:teamID/team-notification-setting',
        method: HTTPMethod.GET,
        params: params('teamID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    patch_team_notification_setting: {
        path: '/teams/:teamID/team-notification-setting',
        method: HTTPMethod.PATCH,
        params: params('teamID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    get_team_high_school_info: {
        path: '/teams/:teamID/high-school-info',
        method: HTTPMethod.GET,
        params: params('teamID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    post_team_high_school_info: {
        path: '/teams/:teamID/high-school-info',
        method: HTTPMethod.POST,
        params: params('teamID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    post_maxpreps_team_import: {
        path: '/integrations/teams/maxpreps/import',
        method: HTTPMethod.POST,
        params: params(),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.POST_MAXPREPS_TEAM_IMPORT,
        outputType: OutputContentType.MAXPREPS_IMPORT_RESULT,
        paginate: false,
    },
    post_me_bats_import_initiate: {
        path: '/me/bats-import/initiate',
        method: HTTPMethod.POST,
        params: params(),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.BATS_IMPORT_INITIATE,
        paginate: false,
    },
    get_team_external_associations: {
        path: '/teams/:teamID/external-associations',
        method: HTTPMethod.GET,
        params: params('teamID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    get_public_team_summary: {
        path: '/teams/:teamID/public-summary',
        method: HTTPMethod.GET,
        params: params('teamID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    get_schedule_events: {
        path: '/teams/:teamID/schedule',
        method: HTTPMethod.GET,
        params: params('teamID'),
        auth: AuthenticationMethod.USER,
        allowedCapabilities: Capabilities.canAccessTeam,
        inputType: InputContentType.NONE,
        outputType: new ListContentType(OutputContentType.EVENT),
        paginate: false,
    },
    post_reimport_external_events: {
        path: '/teams/:teamID/schedule/reimport-external-events',
        method: HTTPMethod.POST,
        params: params('teamID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    post_schedule_events: {
        path: '/teams/:teamID/schedule/events/',
        method: HTTPMethod.POST,
        params: params('teamID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.POST_EVENT,
        outputType: OutputContentType.EVENT,
        paginate: false,
    },
    patch_schedule_event: {
        path: '/teams/:teamID/schedule/events/:eventID/',
        method: HTTPMethod.PATCH,
        params: params('teamID', 'eventID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.PATCH_EVENT,
        outputType: OutputContentType.EVENT,
        paginate: false,
    },
    delete_schedule_event: {
        path: '/teams/:teamID/schedule/events/:eventID/',
        method: HTTPMethod.PATCH,
        params: params('teamID', 'eventID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.PATCH_EVENT,
        outputType: OutputContentType.EVENT,
        paginate: false,
    },
    get_schedule_event_video_stream: {
        path: '/teams/:teamID/schedule/events/:eventID/video-stream/',
        method: HTTPMethod.GET,
        params: params('teamID', 'eventID'),
        auth: AuthenticationMethod.USER,
        allowedCapabilities: Capabilities.canAccessEvent,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.SCHEDULE_EVENT_VIDEO_STREAM,
        paginate: false,
    },
    patch_roster_rollover: {
        path: '/teams/:teamID/rollover',
        method: HTTPMethod.PATCH,
        params: params('teamID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.PATCH_ROSTER_ROLLOVER,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    get_event: {
        path: '/events/:eventID',
        method: HTTPMethod.GET,
        params: params('eventID'),
        auth: AuthenticationMethod.USER,
        allowedCapabilities: Capabilities.canAccessEvent,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.EVENT,
        paginate: false,
    },
    get_event_rsvp: {
        path: '/teams/:teamID/schedule/events/:eventID/rsvp-responses',
        method: HTTPMethod.GET,
        params: params('teamID', 'eventID'),
        auth: AuthenticationMethod.USER,
        allowedCapabilities: Capabilities.canAccessRSVP,
        inputType: InputContentType.NONE,
        outputType: new ListContentType(OutputContentType.RSVP_RESPONSE),
        paginate: false,
    },
    put_event_rsvp_for_player: {
        path: '/teams/:teamID/schedule/events/:eventID/rsvp-responses/player/:playerID',
        method: HTTPMethod.PUT,
        params: params('teamID', 'eventID', 'playerID'),
        allowedCapabilities: Capabilities.canAccessRSVP,
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.PUT_RSVP_RESPONSE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    put_event_rsvp_for_user: {
        path: '/teams/:teamID/schedule/events/:eventID/rsvp-responses/user/:userID',
        method: HTTPMethod.PUT,
        params: params('teamID', 'eventID', 'userID'),
        auth: AuthenticationMethod.USER,
        allowedCapabilities: Capabilities.canAccessRSVP,
        inputType: InputContentType.PUT_RSVP_RESPONSE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    post_schedule_event_series: {
        path: '/teams/:teamID/schedule/event-series/',
        method: HTTPMethod.POST,
        params: params('teamID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.POST_EVENT_SERIES,
        outputType: OutputContentType.EVENT_SERIES,
        paginate: false,
    },
    get_schedule_event_series: {
        path: '/teams/:teamID/schedule/event-series/:seriesID',
        method: HTTPMethod.GET,
        params: params('teamID', 'seriesID'),
        auth: AuthenticationMethod.USER,
        allowedCapabilities: Capabilities.canAccessRSVP,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.EVENT_SERIES,
        paginate: false,
    },
    get_schedule_event_series_events: {
        path: '/teams/:teamID/schedule/event-series/:seriesID/events',
        method: HTTPMethod.GET,
        params: params('teamID', 'seriesID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    patch_schedule_event_series: {
        path: '/teams/:teamID/schedule/event-series/:seriesID/',
        method: HTTPMethod.PATCH,
        params: params('teamID', 'seriesID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.PATCH_EVENT_SERIES,
        outputType: OutputContentType.EVENT_SERIES,
        paginate: false,
    },
    delete_schedule_event_series: {
        path: '/teams/:teamID/schedule/event-series/:seriesID/',
        method: HTTPMethod.PATCH,
        params: params('teamID', 'seriesID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.PATCH_EVENT_SERIES,
        outputType: OutputContentType.EVENT_SERIES,
        paginate: false,
    },
    get_team_game_summaries: {
        path: '/teams/:teamID/game-summaries',
        method: HTTPMethod.GET,
        params: params('teamID'),
        auth: AuthenticationMethod.USER,
        allowedCapabilities: Capabilities.canAccessTeam,
        inputType: InputContentType.NONE,
        outputType: new ListContentType(OutputContentType.GAME_SUMMARY),
        paginate: true,
    },
    get_team_game_summary: {
        path: '/teams/:teamID/game-summaries/:eventID',
        method: HTTPMethod.GET,
        params: params('teamID', 'eventID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.GAME_SUMMARY,
        paginate: false,
    },
    get_batch_simple_scorekeeping_game_data_result: {
        path: '/teams/:teamID/schedule/batch-simple-scorekeeping-data',
        method: HTTPMethod.GET,
        params: params('teamID'),
        auth: AuthenticationMethod.USER,
        allowedCapabilities: Capabilities.canAccessTeam,
        inputType: InputContentType.NONE,
        outputType: new ListContentType(OutputContentType.BATCH_SIMPLE_SCOREKEEPING_GAME_DATA_RESULT),
        paginate: false,
    },
    get_simple_scorekeeping_game_data: {
        path: '/teams/:teamID/schedule/events/:eventId/simple-scorekeeping/game-data',
        method: HTTPMethod.GET,
        params: params('teamID', 'eventId'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.SIMPLE_SCOREKEEPING_GAME_DATA,
        paginate: false,
    },
    post_simple_scorekeeping_game_data: {
        path: '/teams/:teamID/schedule/events/:eventId/simple-scorekeeping/game-data',
        method: HTTPMethod.POST,
        params: params('teamID', 'eventId'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    patch_simple_scorekeeping_game_data: {
        path: '/teams/:teamID/schedule/events/:eventId/simple-scorekeeping/game-data',
        method: HTTPMethod.PATCH,
        params: params('teamID', 'eventId'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    patch_simple_scorekeeping_game_data_after_game: {
        path: '/teams/:teamID/schedule/events/:eventId/simple-scorekeeping/game-data-after-game',
        method: HTTPMethod.PATCH,
        params: params('teamID', 'eventId'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    post_simple_scorekeeping_game_data_after_game: {
        path: '/teams/:teamID/schedule/events/:eventId/simple-scorekeeping/game-data-after-game',
        method: HTTPMethod.POST,
        params: params('teamID', 'eventId'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    post_simple_scorekeeping_share_time: {
        path: '/teams/:teamID/schedule/events/:eventId/simple-scorekeeping/share-time',
        method: HTTPMethod.POST,
        params: params('teamID', 'eventId'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    get_event_bats_scorekeeping_data: {
        path: '/teams/:teamID/schedule/events/:eventId/scorekeeping-data/bats',
        method: HTTPMethod.GET,
        params: params('teamID', 'eventId'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    get_players: {
        path: '/teams/:teamID/players',
        method: HTTPMethod.GET,
        params: params('teamID'),
        auth: AuthenticationMethod.USER,
        allowedCapabilities: Capabilities.canAccessTeam,
        inputType: InputContentType.NONE,
        outputType: new ListContentType(OutputContentType.PLAYER),
        paginate: true,
    },
    post_players: {
        path: '/teams/:teamID/players/',
        method: HTTPMethod.POST,
        params: params('teamID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.POST_PLAYER,
        outputType: OutputContentType.PLAYER,
        paginate: false,
    },
    patch_player: {
        path: '/players/:playerID',
        method: HTTPMethod.PATCH,
        params: params('playerID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.PATCH_PLAYER,
        outputType: OutputContentType.PLAYER,
        paginate: false,
    },
    delete_player: {
        path: '/players/:playerID',
        method: HTTPMethod.DELETE,
        params: params('playerID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    patch_player_family_relationships: {
        path: '/players/:playerID/family-relationships',
        method: HTTPMethod.PATCH,
        params: params('playerID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.PATCH_PLAYER_FAMILY_RELATIONSHIPS,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    get_simple_scorekeeping_team_config: {
        path: '/teams/:teamID/simple-scorekeeping/config',
        method: HTTPMethod.GET,
        params: params('teamID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    post_simple_scorekeeping_team_config: {
        path: '/teams/:teamID/simple-scorekeeping/config',
        method: HTTPMethod.POST,
        params: params('teamID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    post_opponent_team: {
        path: '/teams/:owningTeamId/opponent/',
        method: HTTPMethod.POST,
        params: params('owningTeamId'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.POST_OPPONENT_TEAM,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    get_opponent_team: {
        path: '/teams/:owningTeamID/opponent/:rootTeamID',
        method: HTTPMethod.GET,
        params: params('owningTeamID', 'rootTeamID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.OPPONENT_TEAM,
        paginate: false,
    },
    get_me_user: {
        path: '/me/user',
        method: HTTPMethod.GET,
        params: params(),
        auth: AuthenticationMethod.USER,
        allowedCapabilities: Capabilities.canAccessRSVP,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.USER,
        paginate: false,
    },
    get_me_subscription_information: {
        path: '/me/subscription-information',
        method: HTTPMethod.GET,
        params: params(),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.SUBSCRIPTION_INFORMATION,
        paginate: false,
    },
    get_me_team: {
        path: '/me/teams',
        method: HTTPMethod.GET,
        params: params(),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: new ListContentType(OutputContentType.TEAM),
        paginate: false,
    },
    get_me_team_stats: {
        path: '/teams/:teamID/season-stats',
        method: HTTPMethod.GET,
        params: params(),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.TEAM_SEASON_STATS,
        paginate: false,
    },
    get_player_stats: {
        path: '/teams/:teamID/players/:playerID/stats',
        method: HTTPMethod.GET,
        params: params('teamID', 'playerID'),
        auth: AuthenticationMethod.USER,
        allowedCapabilities: Capabilities.canAccessPlayerProfile,
        inputType: InputContentType.NONE,
        outputType: new ListContentType(OutputContentType.PLAYER_STATS),
        paginate: false,
    },
    get_player_game_stats: {
        path: '/teams/:teamID/schedule/events/:eventID/player-stats',
        method: HTTPMethod.GET,
        params: params('teamID', 'eventID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.PLAYER_GAME_STATS,
        paginate: false,
    },
    put_game_stat_edit_collection: {
        path: '/game-streams/:gameStreamID/game-stat-edit-collection/:lastGameStreamEventID',
        method: HTTPMethod.PUT,
        params: params('gameStreamID', 'lastGameStreamEventID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.PUT_GAME_STAT_EDIT_COLLECTION,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    get_game_stat_edit_collection: {
        path: '/game-streams/:gameStreamID/game-stat-edit-collection/:lastGameStreamEventID',
        method: HTTPMethod.GET,
        params: params('gameStreamID', 'lastGameStreamEventID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.GAME_STAT_EDIT_COLLECTION,
        paginate: false,
    },
    get_me_permissions: {
        path: '/me/permissions',
        method: HTTPMethod.GET,
        params: params(),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    get_me_permissions_v2: {
        path: '/me/permissions/:scopingEntityType/:scopingEntityId',
        method: HTTPMethod.GET,
        params: params('scopingEntityType', 'scopingEntityId'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    get_me_permissions_v2_all: {
        path: '/me/permissions/all',
        method: HTTPMethod.GET,
        params: params(),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    patch_me_password: {
        path: '/me/password',
        method: HTTPMethod.PATCH,
        params: params(),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.PATCH_PASSWORD,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    get_me_external_calendar_sync_url: {
        path: '/me/external-calendar-sync-url',
        method: HTTPMethod.GET,
        params: params(),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    get_me_pending_teams: {
        path: '/me/pending-teams',
        method: HTTPMethod.GET,
        params: params(),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    post_me_client_backup: {
        path: '/me/client-backups',
        method: HTTPMethod.POST,
        params: params(),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    delete_me_relationship_requests: {
        path: '/me/relationship-requests/:teamID',
        method: HTTPMethod.DELETE,
        params: params('teamID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    get_me_twilio_chat: {
        path: '/me/tokens/twilio-chat',
        method: HTTPMethod.GET,
        params: params(),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    get_me_organizations: {
        path: '/me/organizations',
        method: HTTPMethod.GET,
        params: params(),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: new ListContentType(OutputContentType.ORGANIZATION_WITH_ROLE),
        paginate: true,
    },
    post_me_firebase_token: {
        path: '/me/tokens/firebase',
        method: HTTPMethod.POST,
        params: params(),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    get_team_updates: {
        path: '/sync-topics/teams/:teamID/updates',
        method: HTTPMethod.GET,
        params: params('teamID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    get_team_topic_state: {
        path: '/sync-topics/teams/:teamID/topic-state',
        method: HTTPMethod.GET,
        params: params('teamID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    get_global_topic_state: {
        path: '/sync-topics/global/topic-state',
        method: HTTPMethod.GET,
        params: params(),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    get_messaging_channel_image: {
        path: '/channels/:channelId/images/:imageId',
        method: HTTPMethod.GET,
        params: params('channelId', 'imageId'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    get_messaging_channel_images: {
        path: '/channels/:channelId/images',
        method: HTTPMethod.GET,
        params: params('channelId'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    post_messaging_channel_image: {
        path: '/channels/:channelId/images',
        method: HTTPMethod.POST,
        params: params('channelId'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    post_messaging_channel_video: {
        path: '/channels/:channelId/videos',
        method: HTTPMethod.POST,
        params: params('channelId'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    get_messaging_channel_video: {
        path: '/channels/:channelId/videos/:videoId',
        method: HTTPMethod.GET,
        params: params('channelId', 'videoId'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    get_messaging_channel_videos: {
        path: '/channels/:channelId/videos',
        method: HTTPMethod.GET,
        params: params('channelId'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    delete_messaging_media: {
        path: '/channels/:channelId/media/:mediaId',
        method: HTTPMethod.DELETE,
        params: params('channelId', 'mediaId'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    get_simple_scorekeeping_configs: {
        path: '/simple-scorekeeping-configs/',
        method: HTTPMethod.GET,
        params: params(),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    post_provider_user_authenticate: {
        path: '/authentication-provider/:provider/user-authenticate',
        method: HTTPMethod.POST,
        params: params('provider'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    get_bsb_teams: {
        path: '/integrations/teams',
        method: HTTPMethod.GET,
        params: params(),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    post_team_import: {
        path: '/integrations/teams/import',
        method: HTTPMethod.POST,
        params: params(),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    get_bats_player_attributes: {
        path: '/player-attributes/:playerId/bats/',
        method: HTTPMethod.GET,
        params: params('playerId'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    post_bats_player_attributes: {
        path: '/player-attributes/:playerId/bats/',
        method: HTTPMethod.POST,
        params: params('playerId'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.POST_BATS_PLAYER_ATTRIBUTES,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    patch_bats_player_attributes: {
        path: '/player-attributes/:playerId/bats/',
        method: HTTPMethod.PATCH,
        params: params('playerId'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.PATCH_BATS_PLAYER_ATTRIBUTES,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    post_bats_scorekeeping_data: {
        path: '/scorekeeping-data/bats/:batsScorekeepingDataId/',
        method: HTTPMethod.POST,
        params: params('batsScorekeepingDataId'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    patch_bats_scorekeeping_data: {
        path: '/scorekeeping-data/bats/:batsScorekeepingDataId/',
        method: HTTPMethod.PATCH,
        params: params('batsScorekeepingDataId'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    get_bats_scorekeeping_data: {
        path: '/scorekeeping-data/bats/:batsScorekeepingDataId/',
        method: HTTPMethod.GET,
        params: params('batsScorekeepingDataId'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    get_bats_scorekeeping_data_event_ids: {
        path: '/scorekeeping-data/bats/:batsScorekeepingDataId/event-ids',
        method: HTTPMethod.GET,
        params: params('batsScorekeepingDataId'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    post_bats_scorekeeping_event: {
        path: '/scorekeeping-events/bats/:batsScorekeepingEventId/',
        method: HTTPMethod.POST,
        params: params('batsScorekeepingEventId'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    get_bats_scorekeeping_event: {
        path: '/scorekeeping-events/bats/:batsScorekeepingEventId/',
        method: HTTPMethod.GET,
        params: params('batsScorekeepingEventId'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    post_pregame_data: {
        path: '/pregame-data/',
        method: HTTPMethod.POST,
        params: params(),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    get_pregame_data: {
        path: '/pregame-data/:pregameDataId',
        method: HTTPMethod.GET,
        params: params('pregameDataId'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    opponent_search: {
        path: '/search/opponent-import',
        method: HTTPMethod.GET,
        params: params(
            'startAt',
            'name',
            'ageGroup',
            'competitionLevel',
            'city',
            'state',
            'country',
            'season',
            'year',
            'sport',
            'filterSeasons',
            'filterIncludeOlderSeasons',
            'filterCity',
            'filterState',
            'filterLat',
            'filterLong',
            'searchSource',
        ),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.OPPONENT_TEAM_IMPORT_SEARCH_RESULT,
        paginate: false,
    },
    get_maxpreps_school_search: {
        path: '/search/maxpreps-school',
        method: HTTPMethod.GET,
        params: params('name', 'start_at'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.MAXPREPS_SCHOOL_SEARCH_RESULTS,
        paginate: false,
    },
    post_opponent_team_import: {
        path: '/teams/:teamID/opponent/import',
        method: HTTPMethod.POST,
        params: params('teamID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.POST_OPPONENT_TEAM_IMPORT,
        outputType: OutputContentType.OPPONENT_TEAM_ID,
        paginate: false,
    },
    post_opponent_team_legacy_import: {
        path: '/teams/:teamID/opponent/legacy-import',
        method: HTTPMethod.POST,
        params: params('teamID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.POST_OPPONENT_TEAM_LEGACY_IMPORT,
        outputType: OutputContentType.OPPONENT_TEAM_ID,
        paginate: false,
    },
    get_event_best_stream_id: {
        path: '/events/:eventID/best-game-stream-id',
        method: HTTPMethod.GET,
        params: params('eventID'),
        auth: AuthenticationMethod.USER,
        allowedCapabilities: Capabilities.canAccessEvent,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.EVENT_BEST_STREAM_ID,
        paginate: false,
    },
    get_game_stream: {
        path: '/game-streams/:streamID',
        method: HTTPMethod.GET,
        params: params('streamID'),
        auth: AuthenticationMethod.USER,
        allowedCapabilities: Capabilities.canAccessTeam,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.GAME_STREAM,
        paginate: false,
    },
    get_game_stream_events: {
        path: '/game-streams/:streamID/events',
        method: HTTPMethod.GET,
        params: params('streamID'),
        auth: AuthenticationMethod.USER,
        allowedCapabilities: Capabilities.canAccessTeam,
        inputType: InputContentType.NONE,
        outputType: new ListContentType(OutputContentType.GAME_STREAM_EVENT),
        paginate: false,
    },
    get_game_stream_viewer_payload_lite: {
        path: '/game-streams/gamestream-viewer-payload-lite/:eventID',
        method: HTTPMethod.GET,
        params: params('eventID'),
        auth: AuthenticationMethod.USER,
        paginate: false,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.GAME_STREAM_VIEWER_PAYLOAD_LITE,
    },
    get_bats_starting_lineup: {
        path: '/bats-starting-lineups/:lineupId',
        method: HTTPMethod.GET,
        params: params('lineupId'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.BATS_STARTING_LINEUP,
        paginate: false,
    },
    patch_bats_starting_lineup: {
        path: '/bats-starting-lineups/:lineupId',
        method: HTTPMethod.PATCH,
        params: params('lineupId'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.PATCH_BATS_STARTING_LINEUP,
        outputType: OutputContentType.BATS_STARTING_LINEUP,
        paginate: false,
    },
    post_bats_starting_lineup: {
        path: '/bats-starting-lineups/',
        method: HTTPMethod.POST,
        params: params(),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.POST_BATS_STARTING_LINEUP,
        outputType: OutputContentType.BATS_STARTING_LINEUP,
        paginate: false,
    },
    get_latest_bats_starting_lineup: {
        path: '/bats-starting-lineups/latest/:teamID',
        method: HTTPMethod.GET,
        params: params('teamID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.LATEST_BATS_STARTING_LINEUP,
        paginate: false,
    },

    get_player_profile_photo: {
        path: '/players/:playerID/profile-photo',
        method: HTTPMethod.GET,
        params: params('playerID'),
        auth: AuthenticationMethod.USER,
        allowedCapabilities: Capabilities.canAccessPlayerProfile,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.NONE,
        paginate: false,
    },

    get_organization: {
        path: '/organizations/:orgID',
        method: HTTPMethod.GET,
        params: params('orgID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.ORGANIZATION,
        paginate: false,
    },

    get_organization_teams: {
        path: '/organizations/:orgID/teams',
        method: HTTPMethod.GET,
        params: params('orgID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: new ListContentType(OutputContentType.ORGANIZATION_TEAM),
        paginate: true,
    },

    get_organization_users: {
        path: '/organizations/:orgID/users',
        method: HTTPMethod.GET,
        params: params('orgID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.ORGANIZATION_USER_ASSOCIATIONS,
        paginate: false,
    },

    post_organization_schedule_event: {
        path: '/organizations/:orgID/events',
        method: HTTPMethod.POST,
        params: params('orgID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.POST_ORGANIZATION_SCHEDULE_EVENT,
        outputType: OutputContentType.ORGANIZATION_SCHEDULE_EVENT,
        paginate: false,
    },

    post_organization_schedule_event_bulk: {
        path: '/organizations/:orgID/events/bulk',
        method: HTTPMethod.POST,
        params: params('orgID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.POST_ORGANIZATION_SCHEDULE_EVENT_BULK,
        outputType: OutputContentType.NONE,
        paginate: false,
    },

    post_organization_schedule_event_bulk_validate: {
        path: '/organizations/:orgID/events/bulk/validate',
        method: HTTPMethod.POST,
        params: params('orgID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.POST_ORGANIZATION_SCHEDULE_EVENT_BULK_VALIDATE,
        outputType: OutputContentType.ORGANIZATION_SCHEDULE_EVENT_BULK_VALIDATE,
        paginate: false,
    },

    post_organization_teams: {
        path: '/organizations/:orgID/teams',
        method: HTTPMethod.POST,
        params: params('orgID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.POST_ORGANIZATION_TEAMS,
        outputType: OutputContentType.NONE,
        paginate: false,
    },

    patch_organization_schedule_event: {
        path: '/organizations/:orgID/events/:eventID',
        method: HTTPMethod.PATCH,
        params: params('orgID', 'eventID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.PATCH_ORGANIZATION_SCHEDULE_EVENT,
        outputType: OutputContentType.ORGANIZATION_SCHEDULE_EVENT,
        paginate: false,
    },

    get_organization_schedule_event_bulk_upload_template: {
        path: '/organizations/:orgID/events/bulk/template',
        method: HTTPMethod.GET,
        params: params('orgID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.ORGANIZATION_SCHEDULE_EVENT_BULK_UPLOAD_TEMPLATE,
        paginate: false,
    },

    get_organization_leaderboards: {
        path: '/organizations/:orgID/leaderboards',
        method: HTTPMethod.GET,
        params: params('orgID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.ORGANIZATION_LEADERBOARDS,
        paginate: false,
    },

    get_organization_schedule_event_results_export: {
        path: '/organizations/:orgID/events/results/export',
        method: HTTPMethod.GET,
        params: params('orgID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.ORGANIZATION_SCHEDULE_EVENT_RESULTS_EXPORT,
        paginate: false,
    },

    get_player_video_clip_asset_metadata: {
        path: '/teams/:teamID/video-clips/player/:playerID/clips',
        method: HTTPMethod.GET,
        params: params('playerID', 'teamID'),
        auth: AuthenticationMethod.USER,
        allowedCapabilities: Capabilities.canAccessPlayerProfile,
        inputType: InputContentType.NONE,
        outputType: new ListContentType(OutputContentType.VIDEO_CLIP_ASSET_METADATA),
        paginate: true,
    },
    get_video_clip: {
        path: '/teams/:teamID/video-clips/playable-clip/:clipID/clip',
        method: HTTPMethod.GET,
        params: params('teamID', 'clipID'),
        auth: AuthenticationMethod.USER,
        allowedCapabilities: Capabilities.canAccessPlaybackClip,
        inputType: InputContentType.NONE,
        outputType: OutputContentType.VIDEO_CLIP,
        paginate: false,
    },
    get_all_schedule_event_stream_asset_metadata: {
        path: '/teams/:teamID/schedule/events/:eventID/video-stream/assets',
        method: HTTPMethod.GET,
        auth: AuthenticationMethod.USER,
        params: params('teamID', 'eventID'),
        inputType: InputContentType.NONE,
        outputType: new ListContentType(OutputContentType.VIDEO_STREAM_ASSET_METADATA),
        paginate: false,
    },
    get_stream_asset_playback_data: {
        path: '/teams/:teamID/schedule/events/:eventID/video-stream/assets/playback',
        method: HTTPMethod.GET,
        auth: AuthenticationMethod.USER,
        params: params('teamID', 'eventID'),
        inputType: InputContentType.NONE,
        outputType: new ListContentType(OutputContentType.VIDEO_STREAM_ASSET_PLAYBACK_DATA),
        paginate: false,
    },
    get_all_team_stream_asset_metadata: {
        path: '/teams/:teamID/video-stream/assets',
        method: HTTPMethod.GET,
        auth: AuthenticationMethod.USER,
        params: params('teamID'),
        inputType: InputContentType.NONE,
        outputType: new ListContentType(OutputContentType.VIDEO_STREAM_ASSET_METADATA),
        paginate: true,
    },
    patch_stream_asset_metadata: {
        path: '/teams/:teamID/schedule/events/:eventID/video-stream/assets/:assetID/hidden',
        method: HTTPMethod.PATCH,
        auth: AuthenticationMethod.USER,
        params: params('teamID', 'eventID', 'assetID'),
        inputType: InputContentType.PATCH_STREAM_ASSET_METADATA,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    post_team_community_pass: {
        path: '/teams/:teamID/community-pass',
        method: HTTPMethod.POST,
        params: params('teamID'),
        auth: AuthenticationMethod.USER,
        inputType: InputContentType.POST_TEAM_COMMUNITY_PASS,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    get_opponent_teams: {
        path: '/teams/:teamID/opponents',
        method: HTTPMethod.GET,
        auth: AuthenticationMethod.USER,
        params: params('teamID'),
        inputType: InputContentType.NONE,
        outputType: new ListContentType(OutputContentType.OPPONENT_TEAM),
        paginate: true,
    },
    patch_opponent_team: {
        path: '/teams/:teamID/opponent/:opponentTeamID',
        method: HTTPMethod.PATCH,
        auth: AuthenticationMethod.USER,
        params: params('teamID', 'opponentTeamID'),
        inputType: InputContentType.PATCH_OPPONENT_TEAM,
        outputType: OutputContentType.NONE,
        paginate: false,
    },
    get_athlete_profile_by_handle_public: {
        path: '/public/athlete-profile/:handle',
        method: HTTPMethod.GET,
        auth: AuthenticationMethod.NONE,
        params: params('handle'),
        inputType: InputContentType.NONE,
        outputType: OutputContentType.ATHLETE_PROFILE_PUBLIC,
        paginate: false,
    },
    get_athlete_profile_clips_public: {
        path: '/public/athlete-profile/:athleteProfileID/clips',
        method: HTTPMethod.GET,
        auth: AuthenticationMethod.NONE,
        allowedCapabilities: Capabilities.canAccessAthleteProfileClips,
        params: params('athleteProfileID'),
        inputType: InputContentType.NONE,
        outputType: new ListContentType(OutputContentType.ATHLETE_PROFILE_CLIPS_PUBLIC),
        paginate: false,
    },
    get_athlete_profile_authenticated_clip_url_public: {
        path: '/public/athlete-profile/:athleteProfileID/clips/:clipID/authenticatedUrl',
        method: HTTPMethod.GET,
        auth: AuthenticationMethod.NONE,
        allowedCapabilities: Capabilities.canAccessAthleteProfileClips,
        params: params('athleteProfileID', 'clipID'),
        inputType: InputContentType.NONE,
        outputType: OutputContentType.VIDEO_STREAM_PLAYBACK_DATA,
        paginate: false,
    },
    get_athlete_profile_career_stats_public: {
        path: '/public/athlete-profile/:athleteProfileID/career-stats',
        method: HTTPMethod.GET,
        auth: AuthenticationMethod.NONE,
        allowedCapabilities: Capabilities.canAccessAthleteProfileCareerStats,
        params: params('athleteProfileID'),
        inputType: InputContentType.NONE,
        outputType: OutputContentType.ATHLETE_PROFILE_CAREER_STATS_PUBLIC,
        paginate: false,
    },
    get_team_public_profile_id: {
        path: '/teams/:teamID/public-team-profile-id',
        method: HTTPMethod.GET,
        auth: AuthenticationMethod.USER,
        params: params('teamID'),
        inputType: InputContentType.NONE,
        outputType: OutputContentType.TEAM_PUBLIC_PROFILE_ID,
        paginate: false,
    },
});

export type Routes = typeof routes;
export type RouteName = keyof Routes;
